import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';
import { useAuth, getUserRole } from '../../contexts/AuthContext';
import { isFiscalSetorial, getFiscalSetor } from '../../utils/dataIsolation';

export function isPrestadorAtivo(p) {
  if (!p) return false;
  if (p.inativo === 1 || p.inativo === true || p.inativo === '1') return false;
  if (p.finalizado === 1 || p.finalizado === true || p.finalizado === '1' || p.finalizado === 'true') {
    return false;
  }
  return true;
}

function normSetor(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
}

export function parseValorMes(valor) {
  if (valor == null) return 0;
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  let s = String(valor).replace(/[R$\s]/g, '');
  if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (s.includes('.')) {
    const partes = s.split('.');
    if (!(partes.length === 2 && partes[1].length <= 2)) {
      s = s.replace(/\./g, '');
    }
  }
  return parseFloat(s) || 0;
}

export function formatCurrency(n) {
  return Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Dados de leitura do módulo isolado do fiscal (mesmo tenant).
 */
export function useModuloFiscalData(fiscalId) {
  const { unidadeSelecionada, user, rbacRole, fiscalSetor } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fiscal, setFiscal] = useState(null);
  const [contratos, setContratos] = useState([]);
  const [badges, setBadges] = useState({});
  const [qtdNfs, setQtdNfs] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [resF, resP] = await Promise.all([
        apiFetch(withUnidade('/api/fiscais', unidadeSelecionada)),
        apiFetch(withUnidade('/api/prestadores', unidadeSelecionada)),
      ]);
      if (!resF.ok) throw new Error('Erro ao buscar fiscal');
      const fiscais = await resF.json();
      const f = (Array.isArray(fiscais) ? fiscais : []).find(
        (x) => String(x.id) === String(fiscalId)
      );
      if (!f) throw new Error('Fiscal não encontrado nesta unidade.');

      const setor = String(f.setor || '').trim();
      const role = rbacRole || getUserRole(user);
      if (isFiscalSetorial(user, role)) {
        const meuSetor = getFiscalSetor(user, fiscalSetor);
        if (meuSetor && normSetor(meuSetor) !== normSetor(setor)) {
          throw new Error('Acesso restrito: você só pode ver o módulo do seu setor.');
        }
      }

      setFiscal(f);

      let prestadores = [];
      if (resP.ok) prestadores = await resP.json();
      const doSetor = (Array.isArray(prestadores) ? prestadores : [])
        .filter(isPrestadorAtivo)
        .filter((p) => normSetor(p.setor) === normSetor(setor));
      setContratos(doSetor);

      const nums = doSetor.map((c) => c.numeroContrato).filter(Boolean);
      const badgeMap = {};
      nums.forEach((n) => {
        badgeMap[n] = { ad: false, nf: false, av: false };
      });

      await Promise.all(
        nums.map(async (num) => {
          try {
            const path = withUnidade(
              `/api/aditivos?numeroContrato=${encodeURIComponent(num)}`,
              unidadeSelecionada
            );
            const res = await apiFetch(path);
            if (res.ok) {
              const ads = await res.json();
              if (Array.isArray(ads) && ads.length > 0) badgeMap[num].ad = true;
            }
          } catch {
            /* ignore */
          }
        })
      );

      let nfsCount = 0;
      try {
        const resNf = await apiFetch(withUnidade('/api/notas-fiscais', unidadeSelecionada));
        if (resNf.ok) {
          const nfs = await resNf.json();
          const list = Array.isArray(nfs) ? nfs : nfs?.notas || nfs?.data || [];
          const setNums = new Set(nums);
          list.forEach((nf) => {
            const num = nf.numeroContrato || nf.numero_contrato;
            if (setNums.has(num)) {
              nfsCount += 1;
              if (badgeMap[num]) badgeMap[num].nf = true;
            }
          });
        }
      } catch {
        /* ignore */
      }
      setQtdNfs(nfsCount);

      try {
        const resAv = await apiFetch(withUnidade('/api/avaliacoes', unidadeSelecionada));
        if (resAv.ok) {
          const avs = await resAv.json();
          const list = Array.isArray(avs) ? avs : [];
          const setNums = new Set(nums);
          list.forEach((av) => {
            const num = av.numeroContrato || av.numero_contrato;
            if (setNums.has(num) && badgeMap[num]) badgeMap[num].av = true;
          });
        }
      } catch {
        /* ignore */
      }

      setBadges({ ...badgeMap });
    } catch (e) {
      console.error('ModuloFiscal:', e);
      setError(e.message || 'Erro ao carregar módulo do fiscal.');
      setFiscal(null);
      setContratos([]);
    } finally {
      setLoading(false);
    }
  }, [fiscalId, unidadeSelecionada, user, rbacRole, fiscalSetor]);

  useEffect(() => {
    load();
  }, [load]);

  const valorTotal = useMemo(
    () => contratos.reduce((acc, c) => acc + parseValorMes(c.valorMes), 0),
    [contratos]
  );

  return { loading, error, fiscal, contratos, badges, qtdNfs, valorTotal, reload: load };
}
